import { useState } from 'react';
import { ShoppingList, ShoppingListManager } from '@core/shopping';
import { UnitConverter } from '@core/units';

interface ShoppingListViewProps {
  shoppingList: ShoppingList;
  manager: ShoppingListManager;
}

export function ShoppingListView({ shoppingList, manager }: ShoppingListViewProps) {
  const [unitSystem, setUnitSystem] = useState<'imperial' | 'metric'>('imperial');
  const [converter] = useState(() => new UnitConverter());

  const handleExport = () => {
    const text = manager.exportAsText(shoppingList);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopping-list-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    const text = manager.exportAsText(shoppingList);
    await navigator.clipboard.writeText(text);
    alert('Shopping list copied to clipboard!');
  };

  const formatQuantityWithUnit = (quantity: number | null, unit: string | null): string => {
    if (!quantity || !unit) {
      return quantity ? quantity.toString() : '';
    }

    // Try to convert the unit based on system preference
    try {
      const result = converter.convertToSystem(quantity, unit, unitSystem);
      const formatted = result.value % 1 === 0
        ? result.value.toString()
        : result.value.toFixed(2).replace(/\.?0+$/, '');
      return `${formatted} ${result.unit}`;
    } catch {
      // If conversion fails, return original
      const formatted = quantity % 1 === 0
        ? quantity.toString()
        : quantity.toFixed(2).replace(/\.?0+$/, '');
      return `${formatted} ${unit}`;
    }
  };

  // Group items by category
  const byCategory = new Map<string, typeof shoppingList.items>();
  for (const item of shoppingList.items) {
    if (!byCategory.has(item.category)) {
      byCategory.set(item.category, []);
    }
    byCategory.get(item.category)!.push(item);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Shopping List</h2>
            <p className="text-sm text-gray-600 mt-1">
              Generated from {shoppingList.summary.totalRecipes} recipes •
              {shoppingList.summary.pantryItemsUsed > 0 && (
                <> {shoppingList.summary.pantryItemsUsed} items from pantry •</>
              )}
              {' '}{shoppingList.lastUpdated.toLocaleDateString()}
            </p>
          </div>

          <div className="flex gap-2">
            {/* Unit System Toggle */}
            <div className="flex border border-gray-300 rounded-md overflow-hidden">
              <button
                onClick={() => setUnitSystem('imperial')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  unitSystem === 'imperial'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Imperial
              </button>
              <button
                onClick={() => setUnitSystem('metric')}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                  unitSystem === 'metric'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Metric
              </button>
            </div>

            <button
              onClick={handleCopy}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">Total Items</div>
            <div className="text-2xl font-bold text-blue-900">
              {shoppingList.summary.totalItems}
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">Recipes</div>
            <div className="text-2xl font-bold text-green-900">
              {shoppingList.summary.totalRecipes}
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-purple-600 font-medium">Pantry Items Used</div>
            <div className="text-2xl font-bold text-purple-900">
              {shoppingList.summary.pantryItemsUsed}
            </div>
          </div>
        </div>
      </div>

      {/* Recipes Included */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Recipes Included</h3>
        <div className="flex flex-wrap gap-2">
          {shoppingList.recipes.map((recipe) => (
            <span
              key={recipe.id}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
            >
              {recipe.name} ({recipe.servings} servings)
            </span>
          ))}
        </div>
      </div>

      {/* Shopping List Items by Category */}
      <div className="space-y-6">
        {Array.from(byCategory.entries()).map(([category, items]) => (
          <div key={category} className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 uppercase tracking-wide">
              {category}
            </h3>

            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.canonical}
                  className={`flex items-start p-4 rounded-lg ${
                    item.hasConflict ? 'bg-red-50' : 'bg-gray-50'
                  }`}
                >
                  <input type="checkbox" className="mt-1 h-5 w-5 text-blue-600 rounded" />

                  <div className="ml-4 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {item.quantity !== null && item.unit
                          ? `${formatQuantityWithUnit(item.quantity, item.unit)} `
                          : item.quantity !== null
                          ? `${item.quantity} `
                          : ''}
                        {item.displayName}
                      </span>
                      {item.hasConflict && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          ⚠️ Conflict
                        </span>
                      )}
                    </div>

                    <div className="mt-1 space-y-1">
                      {item.inPantry !== null && item.inPantry > 0 && (
                        <div className="text-sm text-gray-600">
                          Have {item.unit
                            ? formatQuantityWithUnit(item.inPantry, item.unit)
                            : item.inPantry} in pantry
                        </div>
                      )}

                      <div className="text-sm text-gray-500">
                        Used in: {item.recipes.join(', ')}
                      </div>

                      {item.hasConflict && item.conflictReason && (
                        <div className="text-sm text-red-600">
                          {item.conflictReason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
